import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import {
	ISupportGitHubCreateIssueRequest,
	ISupportGitHubIssueUpdateRequest,
	ISupportGitHubPanel,
} from '../../models/Support/ISupportGitHub';
import { isNotFoundError } from '../../utils/Support/supportHelpers';
import {
	invalidateTicket,
	SUPPORT_PATHS,
	SUPPORT_POLL_INTERVAL_MS,
	SUPPORT_QUERY_KEYS,
	supportPollingOptions,
} from './supportQueryHelpers';

interface SupportGitHubQueryOptions {
	/** Override the default polling interval (in ms). Pass `false` to disable polling. */
	refetchInterval?: number | false;
}

function invalidateSupportGitHubTicketState(queryClient: QueryClient, ticketId: string) {
	queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.tickets.github(ticketId) });
	invalidateTicket(queryClient, ticketId);
}

export function useSupportGitHubQuery(ticketId: string, options: SupportGitHubQueryOptions = {}) {
	const { refetchInterval = SUPPORT_POLL_INTERVAL_MS } = options;
	const userSession = useUserSession();

	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.tickets.github(ticketId),
		staleTime: 5_000,
		...supportPollingOptions(refetchInterval),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(SUPPORT_PATHS.github.panel(ticketId), 'GET'),
		enabled: !!ticketId,
		meta: {
			// A 404 means the GitHub integration endpoints don't exist in this environment;
			// GitHubPanel renders that as a quiet "not available" state, so suppress the toast.
			toastErrorMessage: (error: Error) => (isNotFoundError(error) ? '' : 'Failed to load linked GitHub issues'),
		},
	});
}

export function useLinkGitHubIssueLinkMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (url: string) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(SUPPORT_PATHS.github.linkIssue(ticketId), 'POST', {
				url,
			}),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useCreateGitHubIssueMutation(ticketId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ISupportGitHubCreateIssueRequest) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.issues(ticketId),
				'POST',
				payload
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useUnlinkGitHubIssueLinkMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.issue(ticketId, linkId),
				'DELETE'
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useUpdateGitHubIssueMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ISupportGitHubIssueUpdateRequest) =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.issue(ticketId, linkId),
				'PATCH',
				payload
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}

export function useSyncGitHubIssueMutation(ticketId: string, linkId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () =>
			userSession.webServiceHelper.callAsync<ISupportGitHubPanel>(
				SUPPORT_PATHS.github.syncIssue(ticketId, linkId),
				'POST'
			),
		onSuccess: () => invalidateSupportGitHubTicketState(queryClient, ticketId),
	});
}
